import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { aiAPI } from '../../services/api';
import { getErrorMessage } from '../../utils/errorHandler';

export const fetchPredictions = createAsyncThunk('ai/predictions', async (params, { rejectWithValue }) => {
  try {
    const response = await aiAPI.fetchPredictions(params);
    return response.data;
  } catch (error) {
    return rejectWithValue(getErrorMessage(error, 'Failed to fetch predictions'));
  }
});

export const fetchAnalytics = createAsyncThunk('ai/analytics', async (params, { rejectWithValue }) => {
  try {
    const response = await aiAPI.fetchAnalytics(params);
    return response.data;
  } catch (error) {
    return rejectWithValue(getErrorMessage(error, 'Failed to fetch analytics'));
  }
});

export const generateReportComments = createAsyncThunk('ai/reportComments', async (data, { rejectWithValue }) => {
  try {
    const response = await aiAPI.generateReportComments(data);
    return response.data.data;
  } catch (error) {
    return rejectWithValue(getErrorMessage(error, 'Failed to generate comments'));
  }
});

export const fetchAnomalies = createAsyncThunk('ai/anomalies', async (params, { rejectWithValue }) => {
  try {
    const response = await aiAPI.fetchAnomalies(params);
    return response.data;
  } catch (error) {
    return rejectWithValue(getErrorMessage(error, 'Failed to fetch anomalies'));
  }
});

export const getPerformancePrediction = createAsyncThunk('ai/performancePrediction', async (studentId, { rejectWithValue }) => {
  try {
    const response = await aiAPI.getPerformancePrediction(studentId);
    return response.data.data;
  } catch (error) {
    return rejectWithValue(getErrorMessage(error, 'Failed to get prediction'));
  }
});

export const getSmartSuggestions = createAsyncThunk('ai/suggestions', async (params, { rejectWithValue }) => {
  try {
    const response = await aiAPI.getSmartSuggestions(params);
    return response.data.data;
  } catch (error) {
    return rejectWithValue(getErrorMessage(error, 'Failed to get suggestions'));
  }
});

const initialState = {
  predictions: [],
  analytics: null,
  anomalies: [],
  reportComments: null,
  performancePrediction: null,
  suggestions: [],
  loading: false,
  error: null,
};

const aiSlice = createSlice({
  name: 'ai',
  initialState,
  reducers: {
    clearAIError: (state) => { state.error = null; },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchPredictions.pending, (state) => { state.loading = true; state.error = null; })
      .addCase(fetchPredictions.fulfilled, (state, action) => { state.loading = false; state.predictions = action.payload.data || action.payload; })
      .addCase(fetchPredictions.rejected, (state, action) => { state.loading = false; state.error = action.payload; })
      .addCase(fetchAnalytics.pending, (state) => { state.loading = true; })
      .addCase(fetchAnalytics.fulfilled, (state, action) => { state.loading = false; state.analytics = action.payload.data || action.payload; })
      .addCase(fetchAnalytics.rejected, (state, action) => { state.loading = false; state.error = action.payload; })
      .addCase(generateReportComments.fulfilled, (state, action) => { state.reportComments = action.payload; })
      .addCase(fetchAnomalies.fulfilled, (state, action) => { state.anomalies = action.payload.data || action.payload; })
      .addCase(getPerformancePrediction.fulfilled, (state, action) => { state.performancePrediction = action.payload; })
      .addCase(getSmartSuggestions.fulfilled, (state, action) => { state.suggestions = action.payload; });
  },
});

export const { clearAIError } = aiSlice.actions;
export default aiSlice.reducer;
